<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import Icon from "@/design/primitives/Icon.vue";
import Menu from "@/design/primitives/Menu.vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import RichText from "@/shared/rich-text/RichText.vue";
import {
	type DocumentVersion,
	documentVersions,
	readDocument,
	readDocumentBytes,
} from "../api/document-content";
import type { TurnAttachment } from "../api/turn-attachments";
import { type ChatDocument, documentKind, isolatedHtml } from "../composables/chat-document";
import { documentTable } from "../composables/document-table";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import ChatDocumentComparison from "./ChatDocumentComparison.vue";
import ChatDocumentEditor from "./ChatDocumentEditor.vue";

const props = defineProps<{
	document?: ChatDocument | null;
	file?: TurnAttachment | null;
	path?: string | null;
	revision?: number;
}>();
const emit = defineEmits<{ close: []; revise: [text: string] }>();
const { t } = useAgentsI18n();
const uploads = inject(UPLOAD_PORT, null);
const { isMobile } = useBreakpoint();
// Phones either leave a PDF iframe blank (Android) or show only page one (iOS): they get a link instead.
const inlinePdf = computed(() => !isMobile.value && navigator.pdfViewerEnabled !== false);
const source = ref(false);
const reload = ref(0);
const editing = ref<{ file: DocumentVersion; content: string } | null>(null);
const comparing = ref(false);
const otherVersion = computed(() =>
	versions.value.find((version) => version.id !== selected.value?.id),
);
function edit(): void {
	if (selected.value) editing.value = { file: { ...selected.value }, content: text.value };
}
function saved(): void {
	editing.value = null;
	reload.value++;
}

const pdfUrl = ref("");
const fileUrl = ref("");
const versions = ref<DocumentVersion[]>([]);
const versionId = ref("");
const selected = computed(() => versions.value.find((v) => v.id === versionId.value));
const activeFile = computed(() =>
	props.path && selected.value
		? {
				id: selected.value.id,
				name: selected.value.path,
				mime: selected.value.mimeType,
				sizeBytes: selected.value.sizeBytes,
			}
		: props.file,
);
const selection = ref("");
const body = ref<HTMLElement | null>(null);
const frame = ref<HTMLIFrameElement | null>(null);
let historyController: AbortController | null = null;
function captureSelection(): void {
	const range = window.getSelection();
	selection.value =
		range?.anchorNode &&
		range.focusNode &&
		body.value?.contains(range.anchorNode) &&
		body.value.contains(range.focusNode)
			? range.toString().slice(0, 4000).trim()
			: "";
}
function receiveSelection(event: MessageEvent): void {
	if (
		event.source !== frame.value?.contentWindow ||
		event.data?.type !== "ben-document-selection" ||
		typeof event.data.text !== "string"
	)
		return;
	selection.value = event.data.text.slice(0, 4000).trim();
}
onMounted(() => window.addEventListener("message", receiveSelection));
function revise(): void {
	emit("revise", t("document.reviseRequest", { name: name.value, text: selection.value }));
}
function restore(): void {
	if (selected.value)
		emit(
			"revise",
			t("document.restoreRequest", { name: name.value, version: selected.value.version }),
		);
}
const content = ref("");
const imageUrl = ref("");
const loading = ref(false);
const historyLoading = ref(false);
const failed = ref(false);
const copyDone = ref(false);
let controller: AbortController | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
const html = ref("");
const name = computed(
	() => props.path || activeFile.value?.name || props.document?.name || t("attach.file"),
);
const kind = computed(() =>
	activeFile.value
		? documentKind(activeFile.value.name, activeFile.value.mime)
		: props.path
			? documentKind(props.path)
			: props.document?.kind,
);
const text = computed(() =>
	props.path || activeFile.value ? content.value : (props.document?.content ?? ""),
);
watch(text, () => {
	selection.value = "";
	copyDone.value = false;
});
const table = computed(() => (kind.value === "csv" ? documentTable(text.value) : null));
const image = computed(() => activeFile.value?.mime.startsWith("image/"));

const actions = computed(() => {
	const disabled = loading.value || historyLoading.value || failed.value;
	const items = [];
	if (selected.value && kind.value !== "pdf" && !image.value) {
		if (selected.value.id === versions.value[0]?.id)
			items.push({ value: "edit", label: t("document.edit"), disabled });
		if (otherVersion.value)
			items.push({
				value: "compare",
				label: t(comparing.value ? "document.preview" : "document.compare"),
				disabled,
			});
	}

	if (!image.value && kind.value !== "pdf")
		items.push({ value: "copy", label: t(copyDone.value ? "chat.copied" : "chat.copy"), disabled });
	return items;
});
function action(value: string): void {
	if (value === "edit") edit();
	else if (value === "compare") comparing.value = !comparing.value;
	else if (value === "copy") void copy();
}

watch(
	[() => activeFile.value?.id, () => props.document?.name],
	async ([id]) => {
		controller?.abort();
		if (pdfUrl.value) URL.revokeObjectURL(pdfUrl.value);
		pdfUrl.value = "";
		fileUrl.value = "";
		comparing.value = false;
		const current = new AbortController();
		controller = current;
		content.value = "";
		imageUrl.value = "";
		failed.value = false;
		loading.value = !!id;
		source.value = false;
		if (!id) return;
		try {
			if (!uploads) throw new Error("no upload port");
			const hosted = await uploads.urlFor(id);
			const url = new URL(hosted.url, window.location.origin);
			if (!["https:", "http:"].includes(url.protocol)) throw new Error("invalid file URL");
			if (current.signal.aborted) return;
			fileUrl.value = url.href;
			if (kind.value === "pdf") {
				// No inline viewer: the fallback links the hosted URL, so the bytes are never read.
				if (!inlinePdf.value) return;
				const bytes = await readDocumentBytes(uploads, id, current.signal, 25 * 1024 * 1024);
				if (!current.signal.aborted)
					pdfUrl.value = URL.createObjectURL(
						new Blob([bytes as BlobPart], { type: "application/pdf" }),
					);
			} else if (image.value) {
				imageUrl.value = fileUrl.value;
			} else {
				const result = await readDocument(uploads, id, current.signal);
				if (!current.signal.aborted) content.value = result;
			}
		} catch {
			if (!current.signal.aborted) failed.value = true;
		} finally {
			if (!current.signal.aborted) loading.value = false;
		}
	},
	{ immediate: true },
);

watch(
	() => [props.path, props.revision, reload.value],
	async () => {
		historyController?.abort();
		versions.value = [];
		versionId.value = "";
		selection.value = "";
		historyLoading.value = false;
		if (!props.path) return;
		const current = new AbortController();
		historyController = current;
		historyLoading.value = true;
		failed.value = false;
		try {
			const result = await documentVersions(props.path, current.signal);
			if (current.signal.aborted) return;
			versions.value = result;
			versionId.value = result[0]?.id ?? "";
			if (!result.length) throw new Error("document missing");
		} catch {
			if (!current.signal.aborted) failed.value = true;
		} finally {
			if (!current.signal.aborted) historyLoading.value = false;
		}
	},
	{ immediate: true },
);

// Refresh at most twice a second; restarting an iframe for every token makes it unusable.
watch(
	[text, kind],
	() => {
		if (timer) return;
		timer = setTimeout(() => {
			timer = undefined;
			html.value = kind.value === "html" ? isolatedHtml(text.value) : "";
		}, 500);
	},
	{ immediate: true },
);
onBeforeUnmount(() => {
	controller?.abort();
	historyController?.abort();
	if (pdfUrl.value) URL.revokeObjectURL(pdfUrl.value);
	window.removeEventListener("message", receiveSelection);
	if (timer) clearTimeout(timer);
});

async function copy(): Promise<void> {
	try {
		await navigator.clipboard.writeText(text.value);
		copyDone.value = true;
	} catch {
		failed.value = true;
	}
}
async function download(): Promise<void> {
	try {
		const url =
			activeFile.value && uploads
				? (await uploads.urlFor(activeFile.value.id, { download: true })).url
				: URL.createObjectURL(
						new Blob([text.value], {
							type: kind.value === "html" ? "text/html" : "text/plain;charset=utf-8",
						}),
					);
		const link = document.createElement("a");
		link.href = url;
		link.download = name.value.split("/").pop() || "document";
		link.rel = "noopener";
		document.body.appendChild(link);
		link.click();
		link.remove();
		if (url.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(url), 1000);
	} catch {
		failed.value = true;
	}
}
</script>

<template>
	<section class="flex h-full min-h-0 min-w-0 flex-col bg-surface" :aria-label="t('document.heading')" data-testid="chat-document" @keydown.esc.stop="emit('close')">
		<header class="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
			<span class="min-w-0 flex-1 truncate text-sm font-medium" :title="name">{{ name }}</span>
			<button type="button" class="inline-flex items-center justify-center rounded p-2 hover:bg-bg-muted max-md:size-11" :aria-label="t('document.close')" @click="emit('close')"><Icon name="close" class="size-4" /></button>
		</header>
		<div class="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-3 py-2 text-xs">
			<select v-if="versions.length" v-model="versionId" :aria-label="t('document.versions')" class="max-w-32 rounded border border-border bg-surface p-1"><option v-for="version in versions" :key="version.id" :value="version.id">{{ t('document.version', { version: version.version }) }}</option></select>
			<button v-if="selected && selected.id !== versions[0]?.id" type="button" class="text-accent" @click="restore">{{ t('document.restore') }}</button>
			<button v-if="selection" type="button" class="text-accent" @click="revise">{{ t('document.revise') }}</button>
			<button v-if="!image && kind !== 'pdf'" type="button" class="text-accent" :aria-pressed="source" @click="source = !source">{{ source ? t('document.preview') : t('document.source') }}</button>
			<button v-if="kind !== 'pdf' || inlinePdf" type="button" class="text-accent disabled:opacity-50" :disabled="loading || historyLoading || (!activeFile && document?.status !== 'ready')" @click="download">{{ t('attach.download') }}</button>
			<a v-if="fileUrl && !loading && !historyLoading" :href="fileUrl" target="_blank" rel="noopener noreferrer" class="text-accent">{{ t("document.openTab") }}</a>
            <Menu v-if="actions.length" :items="actions" :label="t('document.actions')" align="end" @select="action">
                <template #trigger><button type="button" class="ml-auto rounded p-1 hover:bg-bg-muted" :aria-label="t('document.actions')"><Icon name="more" class="size-4" /></button></template>
            </Menu>
			<span v-if="!path && !activeFile && document?.status !== 'ready'" class="ml-auto text-fg-subtle">{{ t(document?.status === 'writing' ? 'document.writing' : 'document.incomplete') }}</span>
		</div>
		<p v-if="loading || historyLoading" role="status" class="p-4 text-sm text-fg-muted">{{ t('document.loading') }}</p>
		<p v-else-if="failed" role="alert" class="p-4 text-sm text-danger">{{ t('document.failed') }}</p>
		<ChatDocumentComparison v-else-if="comparing && selected && otherVersion" :selected="selected" :other="otherVersion" :content="text" />
		<div v-else-if="image" class="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4"><img :src="imageUrl" :alt="name" class="max-h-full max-w-full object-contain" @error="failed = true" /></div>
		<iframe v-else-if="kind === 'pdf' && pdfUrl && inlinePdf" :src="pdfUrl" :title="name" class="min-h-0 w-full flex-1 border-0" />
		<div v-else-if="kind === 'pdf'" class="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
			<Icon name="file" class="size-10 text-fg-subtle" />
			<a v-if="fileUrl || pdfUrl" :href="fileUrl || pdfUrl" target="_blank" rel="noopener noreferrer" class="rounded-lg bg-accent px-5 py-3 text-sm font-medium text-fg-on-accent">{{ t("document.openPdf") }}</a>
			<button type="button" class="px-4 py-2 text-sm text-accent capitalize" @click="download">{{ t("attach.download") }}</button>
		</div>
		<iframe ref="frame" v-else-if="kind === 'html' && !source" :srcdoc="html" sandbox="allow-scripts" referrerpolicy="no-referrer" :title="name" class="min-h-0 w-full flex-1 border-0 bg-white" />
		<div v-else ref="body" class="min-h-0 flex-1 overflow-auto p-5" @mouseup="captureSelection" @keyup="captureSelection">
			<template v-if="table && !source">
 <p v-if="table.truncated" class="mb-2 text-xs text-fg-muted">{{ t('document.tableLimit') }}</p>
 <table class="w-full border-collapse text-left text-xs"><tbody><tr v-for="(row, rowIndex) in table.rows" :key="rowIndex"><td v-for="(cell, columnIndex) in row" :key="columnIndex" class="max-w-80 whitespace-pre-wrap break-words border border-border px-3 py-2" :class="rowIndex === 0 ? 'bg-bg-muted font-semibold' : ''">{{ cell }}</td></tr></tbody></table>
 </template>
 <RichText v-else-if="kind === 'markdown' && !source" :text="text" />
			<pre v-else class="whitespace-pre-wrap break-words font-mono text-xs">{{ text }}</pre>
		</div>
 <ChatDocumentEditor v-if="editing" :file="editing.file" :content="editing.content" @close="editing = null" @saved="saved" />
	</section>
</template>
